<template>
	<v-card class="px-6 py-6">
		<div class="flex flex-row justify-between">
			<h2 class="text-2xl font-medium mb-4">Adauga client</h2>
			<p v-if="isRoCompany" class="text-sm text-blue-500 font-semibold">ANAF API</p>
		</div>
		<v-form ref="addClientForm" v-model="validClientForm" @submit.prevent="addClient">
			<div class="bg-gray-100 px-4 py-0.5 rounded-xl mb-4">
				<v-radio-group v-model="firma" row>
					<v-radio label="Persoana fizica" color="teal" :value="false"></v-radio>
					<v-radio label="Persoana juridica" color="teal" :value="true"></v-radio>
				</v-radio-group>
			</div>
			<div v-if="firma">
				<v-select v-model="client.tara" class="w-1/2 pr-2" :items="countryItems" item-text="name"
					item-value="code" label="Tara" outlined dense :rules="[rules.required]">
					<template v-slot:item="{ item }">
						<span class="mr-3">{{ item.flag }}</span>
						<span>{{ item.name }}</span>
					</template>
					<template v-slot:selection="{ item }">
						<span class="mr-3">{{ item.flag }}</span>
						<span>{{ item.name }}</span>
					</template>
				</v-select>
				<div class="grid grid-cols-2 gap-4">
					<div class="flex flex-row justify-start gap-1">
						<v-text-field ref="cuiField" v-model="client.cui" :label="companyFields.vatLabel" type="text"
							:hint="companyFields.vatHint" outlined dense
							:rules="[rules.required, rules.vat]"></v-text-field>
						<v-btn v-if="isRoCompany" class="teal lighten-2" text dark :loading="loadingCompanyDetails"
							@click="getCompanyData">Cauta</v-btn>
					</div>
					<v-text-field :label="companyFields.regLabel" :hint="companyFields.regHint" text outlined dense
						:rules="isRoCompany ? [rules.required] : []" v-model="client.reg_com"></v-text-field>
				</div>
			</div>
			<div class="grid grid-cols-2 gap-x-4">
				<v-text-field v-if="firma" label="Denumire" text outlined dense :rules="[rules.required]"
					v-model="client.nume"></v-text-field>
				<v-text-field :label="firma ? 'Nume persoana' : 'Nume si prenume'" text outlined dense
					:rules="firma ? [] : [rules.required]" v-model="client.persoana"></v-text-field>
				<v-text-field class="col-span-2" label="Adresa" text outlined dense
					v-model="client.adresa"></v-text-field>
				<v-text-field label="Email" text outlined dense :rules="[rules.email, rules.isEmailOrPhone]"
					v-model="client.email"></v-text-field>
				<v-text-field label="Telefon" text outlined dense :rules="[rules.phone, rules.isEmailOrPhone]"
					v-model="client.tel"></v-text-field>
			</div>
			<v-alert v-if="visibleWarnings && warningListActive.length" border="left" colored-border type="warning"
				dense elevation="1">
				<p class="font-medium my-1">Nu ai completat: <span v-for="item, idx in warningListActive" :key="idx"
						class="text-yellow-600 font-semibold">{{ item
						}}<span v-if="idx != warningList.length - 1">, </span></span></p>
				<p class="leading-6 mb-1">
					Datele clientului vor trebui actualizate pe parcurs pentru a putea folosi toate functiile de
					trimitere si generare a documentelor.
				</p>
			</v-alert>
			<v-alert v-if="duplicates.length" border="left" colored-border :type="firma ? 'error' : 'warning'"
				dense elevation="1">
				<p class="font-medium my-1">{{ duplicatesTitle }}</p>
				<div v-for="match in duplicates" :key="match.id_client"
					class="flex flex-row justify-between items-center gap-2 py-1">
					<div class="leading-5">
						<p class="font-semibold mb-0">{{ match.nume }}<span v-if="match.persoana"> — {{ match.persoana
								}}</span></p>
						<p class="text-sm text-main-grey mb-0">
							<span v-if="match.tel">tel: {{ match.tel }}</span>
							<span v-if="match.email"> · {{ match.email }}</span>
							<span v-if="match.cui"> · {{ match.cui }}</span>
						</p>
					</div>
					<v-btn v-if="canPickClient" color="teal" text small class="uppercase flex-shrink-0"
						@click="useExistingClient(match)">Foloseste</v-btn>
					<router-link v-else :to="`/clienti/single/${match.id_client}`"
						class="text-sm font-semibold uppercase flex-shrink-0">Vezi</router-link>
				</div>
				<p class="text-sm leading-5 mt-2 mb-1">
					<span v-if="canPickClient">Foloseste clientul existent</span><span v-else>Deschide clientul
						existent</span> sau modifica datele pentru a putea salva.
				</p>
			</v-alert>
			<div class="flex flex-row justify-end gap-2 mt-4">
				<v-btn color="blue-grey" class="uppercase" dark large @click="closeModal">Anuleaza</v-btn>
				<v-btn color="teal" class="uppercase" large type="submit" :dark="!duplicates.length"
					:disabled="!!duplicates.length">Salveaza</v-btn>
			</div>
		</v-form>
	</v-card>
</template>

<script>
import flags from "@/assets/countries-flags.json"
import { companyCountries, getCompanyFields, normalizeVatNo } from "@/utils"

export default {
	name: "AddClient",
	data() {
		return {
			validClientForm: false,
			companyDataLoading: false,
			visibleWarnings: false,
			warningList: [],
			duplicates: [],
			firma: false,
			client: {
				nume: null,
				cui: null,
				reg_com: null,
				adresa: null,
				oras: null,
				tara: 'ro',
				persoana: null,
				email: null,
				tel: null,
			},
			info: {},
			rules: {
				required: value => !!value || 'Câmp obligatoriu',
				vat: value => !value || this.companyFields.vatFormat.test(value.replace(/[\s.-]/g, "")) || 'Cod fiscal invalid',
				regcom: value => !value || /^([J|j]|[F|f]|[C|c])[0-9]{2}\/[0-9]+\/(19|20)([0-9]{2})$/.test(value) || 'Număr înregistrare incorect',
				email: value => !value || /.+@.+\..+/.test(value) || "Adresa email invalida",
				isEmailOrPhone: () => this.isEmailOrPhone || "Email sau telefon obligatoriu",
				phone: value => !value || /(\(?\+?\d{1,4}\)?\s?)?\d{6,12}/.test(value) || "Număr incorect",
			}
		};
	},
	props: ["clientData"],
	computed: {
		user() {
			return this.$store.getters.loggedUser;
		},
		countryItems() {
			return companyCountries.map(country => ({
				...country,
				flag: flags[country.code.toUpperCase()].emoji
			}))
		},
		companyFields() {
			return getCompanyFields(this.client.tara)
		},
		isRoCompany() {
			return this.firma && this.client.tara == 'ro'
		},
		loadingCompanyDetails() {
			return this.$store.getters.loadingCompanyDetails
		},
		isEmailOrPhone() {
			return !!this.client.tel || !!this.client.email
		},
		// Preselecting only means something where the modal was opened to fill a client field -
		// AddOferta and AddOfertaProductie listen for clientSelected, the contacts list does not.
		canPickClient() {
			return !!this.$listeners.clientSelected
		},
		duplicatesTitle() {
			if (this.firma) {
				return 'Clientul exista deja:'
			}
			const byPhone = this.duplicates.every(match => match.match_on === 'tel')
			return byPhone
				? 'Acest numar de telefon este deja folosit de:'
				: 'Exista deja un client cu aceste date:'
		},
		warningListActive: {
			get() {
				const warnings = []
				if (this.firma && !this.client.persoana) {
					warnings.push('nume persoana')
				}
				// pe firmele din UE numarul de inregistrare se completeaza manual, deci doar avertizam
				if (this.firma && !this.isRoCompany && !this.client.reg_com) {
					warnings.push(this.companyFields.regLabel.toLowerCase())
				}
				if (!this.client.adresa) {
					warnings.push('adresa')
				}
				if (!this.client.email) {
					warnings.push('emailul')
				}
				if (!this.client.tel) {
					warnings.push('telefonul')
				}
				this.warningList = warnings
				return warnings
			},
			set() {
				this.warningList = []
			}
		}
	},
	watch: {
		firma(newVal, oldVal) {
			if (newVal != oldVal) {
				this.$refs.addClientForm.resetValidation();
				this.visibleWarnings = false
				this.warningList = []
			}
		},
		"client.tara"(newVal, oldVal) {
			// campurile fiscale au alt format in fiecare tara, validarea veche nu mai e valabila
			if (newVal != oldVal) {
				this.$refs.addClientForm.resetValidation();
				this.visibleWarnings = false
				this.warningList = []
			}
		},
		// Any edit invalidates what was checked, so the matches are dropped and the next save
		// checks again - otherwise a corrected phone number would still be saved past a warning
		// raised for the old one.
		client: {
			deep: true,
			handler() {
				this.resetDuplicates()
			}
		}
	},
	methods: {
		getLowercaseAddress(str) {
			str = str.toLowerCase();
			const words = str.split(" ");
			for (let i = 0; i < words.length; i++) {
				words[i] = words[i][0].toUpperCase() + words[i].substr(1);
			}
			return words.join(" ");
		},
		loadClientData() {
			this.client.cui = this.clientData.codfiscal
			this.client.nume = this.clientData.nume
			this.client.email = this.clientData.email
			this.client.tel = this.clientData.tel
			this.client.persoana = this.clientData.persoana

			if (this.clientData.nume != "") {
				// boolean, not "true": the radio group binds boolean values, so a string left
				// neither option selected, and +this.firma sent NaN - a null firma column - for
				// every client prefilled from a web request.
				this.firma = true;
			}
		},
		replaceSpecialCh(str) {
			if (str) {
				return str
					.replace(/ș|Ș/g, "s")
					.replace(/ț|Ț/g, "t")
					.replace(/î|Î/g, "i")
					.replace(/ă|â|Â|Ă/g, "a")
			}
		},
		async getCompanyData() {
			// check if cui is valid format
			const validCUI = await this.$refs.cuiField.validate();

			if (validCUI) {
				let cui = this.client.cui
				cui = cui.replace(/\D/g, "");
				const companyDetails = await this.$store.dispatch("fetchCompanyDetailsFromAnaf", cui);
				if (companyDetails === undefined) {
					alert('Nu au fost gasite date!')
				} else {
					this.client.nume = companyDetails.date_generale.denumire;
					this.client.adresa = this.getLowercaseAddress(companyDetails.date_generale.adresa);
					this.client.reg_com = companyDetails.date_generale.nrRegCom;
					this.client.cui = `${companyDetails.inregistrare_scop_Tva.scpTVA ? 'RO' : ''}${companyDetails.date_generale.cui}`
				}
			} else {
				alert("verificati CUI/CIF!");
			}

			// if (validCUI) {
			// 	this.companyDataLoading = true
			// 	let cui = this.client.cui
			// 	cui = cui.replace(/\D/g, "");
			// 	console.log('api call')
			// 	this.$http
			// 		.get(process.env.VUE_APP_OPEN_API_HOST + cui, {
			// 			headers: { "x-api-key": process.env.VUE_APP_OPEN_API_KEY },
			// 		})
			// 		.then(
			// 			function (response) {
			// 				this.client.nume = response.body.denumire;
			// 				this.client.adresa = response.body.adresa + ", jud: " + response.body.judet;
			// 				this.client.reg_com = response.body.numar_reg_com;
			// 				this.companyDataLoading = false
			// 			},
			// 			(response) => {
			// 				if (response.status == 404 || response.status == 202) {
			// 					alert("CUI/CIF negasit!");
			// 				}
			// 			}
			// 		)
			// 		.catch(function (error) {
			// 			this.companyDataLoading = false
			// 			console.log("Errore: " + error.status + " " + error.statusText);
			// 		});

			// } else {
			// 	alert("verificati CUI/CIF!");
			// 	this.companyDataLoading = false
			// }
		},
		closeModal() {
			this.firma = false
			this.client = {
				nume: null,
				cui: null,
				reg_com: null,
				adresa: null,
				oras: null,
				tara: 'ro',
				persoana: null,
				email: null,
				tel: null
			}
			this.visibleWarnings = false
			this.resetDuplicates()
			this.$refs.addClientForm.resetValidation();
			this.$emit('closeModal')
		},
		resetDuplicates() {
			this.duplicates = []
		},
		// Hands the client that was already on file back to the form that opened the modal, so no
		// second row is created. The whole selection is emitted as one object because the client
		// field is a v-autocomplete: it redraws when its value changes by reference, and the
		// mutation the add path relies on only shows up because that path also refetches the
		// item list. Nothing is refetched here - the client is already in it.
		useExistingClient(match) {
			this.$store.commit("loadReturnClientId", match.id_client)
			this.$emit("clientSelected", { value: match.id_client, text: match.nume })
			this.$emit("clientName", match.nume)
			this.closeModal()
		},
		async addClient() {
			const valid = await this.$refs.addClientForm.validate();

			if (valid) {
				if (!this.visibleWarnings && this.warningListActive.length) {
					this.visibleWarnings = true
					return
				}
				const isAgentVanzari = this.user.rol == 'agent' && this.user.departament == 'vanzari'

				let newClient = {
					firma: +this.firma,
					nume: this.client.nume,
					cui: this.firma ? normalizeVatNo(this.client.cui, this.client.tara) : this.client.cui,
					reg_com: this.client.reg_com,
					adresa: this.replaceSpecialCh(this.client.adresa),
					oras: this.client.oras,
					client_country: this.firma ? this.client.tara : null,
					persoana: this.client.persoana,
					email: this.client.email,
					tel: this.client.tel,
					agent: isAgentVanzari ? this.user.id_user : null
				};
				if (!newClient.nume) {
					newClient.nume = newClient.persoana;
					newClient.persoana = null;
				}

				let duplicates = []
				try {
					duplicates = await this.$store.dispatch("checkDuplicateClient", newClient);
				} catch (error) {
					// Saving past a check that never ran is how duplicates got in, so the client
					// is not added until the check has actually answered.
					alert('Nu am putut verifica daca acest client exista deja. Incearca din nou.')
					return
				}

				// Listing them disables Salveaza; editing any field clears them and allows a
				// retry, so the way out is the existing client or different data.
				if (duplicates.length) {
					this.duplicates = duplicates
					return
				}

				this.$store.dispatch("addClient", newClient);
				this.$emit("clientName", newClient.nume);
				// console.log(newClient)
				this.closeModal()
				this.client = {};
			}
		}
	},
	created: function () {
		if (this.clientData != undefined) {
			this.loadClientData();
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.info i {
	float: left;
	margin-top: 15px;
}

.info p {
	/* display: inline-block; */
	margin-left: 50px;
}
</style>
